import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Settings, Server, Lock, Mail, Hash, CheckCircle2, XCircle, Loader2, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";

export function SmtpConfig({ smtpConfig, onConfigChange }) {
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const loadConfigs = useCallback(async () => {
    try {
      const res = await api.get("/smtp-configs");
      setSavedConfigs(res.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (open) loadConfigs();
  }, [open, loadConfigs]);

  const handleTest = async () => {
    if (!smtpConfig.host || !smtpConfig.email || !smtpConfig.password) {
      toast.error("Remplissez tous les champs SMTP");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post("/test-smtp", smtpConfig);
      setTestResult(res.data);
      if (res.data.success) {
        toast.success("Connexion SMTP réussie");
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error("Erreur de connexion");
      setTestResult({ success: false, message: "Erreur réseau" });
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!smtpConfig.host || !smtpConfig.email) {
      toast.error("Remplissez les champs obligatoires");
      return;
    }
    setSaving(true);
    try {
      await api.post("/smtp-config", smtpConfig);
      toast.success("Configuration sauvegardée");
      loadConfigs();
    } catch {
      toast.error("Erreur de sauvegarde");
    }
    setSaving(false);
  };

  const handleLoadConfig = async (configId) => {
    try {
      const res = await api.get(`/smtp-config/${configId}`);
      if (!res.data.error) {
        onConfigChange(res.data);
        toast.success("Configuration chargée");
        setShowSaved(false);
      }
    } catch {
      toast.error("Erreur de chargement");
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      await api.delete(`/smtp-config/${configId}`);
      toast.success("Configuration supprimée");
      loadConfigs();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const updateField = (field, value) => {
    onConfigChange({ ...smtpConfig, [field]: field === "port" ? parseInt(value) || 0 : value });
  };

  const isConfigured = smtpConfig.host && smtpConfig.email && smtpConfig.password;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 rounded-full hover:scale-105 active:scale-95 transition-transform"
          data-testid="smtp-config-button"
        >
          <Settings className="w-4 h-4" />
          SMTP
          {isConfigured && (
            <span className="w-2 h-2 rounded-full bg-emerald-500 ml-1" />
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-[420px] overflow-y-auto" data-testid="smtp-config-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Server className="w-5 h-5 text-primary" />
            Configuration SMTP
          </SheetTitle>
          <SheetDescription>
            Configurez votre serveur SMTP pour l'envoi d'emails
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-6">
          {/* Saved configs */}
          {savedConfigs.length > 0 && (
            <div>
              <button
                className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors w-full"
                onClick={() => setShowSaved(!showSaved)}
                data-testid="show-saved-configs-button"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showSaved ? "rotate-180" : ""}`} />
                Configurations sauvegardées ({savedConfigs.length})
              </button>
              {showSaved && (
                <div className="mt-2 space-y-2">
                  {savedConfigs.map((cfg) => (
                    <div key={cfg.id} className="flex items-center gap-2 p-2 rounded-lg border border-zinc-100 bg-zinc-50">
                      <button
                        className="flex-1 text-left text-xs"
                        onClick={() => handleLoadConfig(cfg.id)}
                        data-testid={`load-config-${cfg.id}`}
                      >
                        <p className="font-medium text-zinc-700">{cfg.name || cfg.host}</p>
                        <p className="text-zinc-400">{cfg.email}</p>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-zinc-400 hover:text-red-500"
                        onClick={() => handleDeleteConfig(cfg.id)}
                        data-testid={`delete-config-${cfg.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="smtp-name" className="text-sm flex items-center gap-2">
              Nom (optionnel)
            </Label>
            <Input
              id="smtp-name"
              value={smtpConfig.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="Ex: Mon Gmail"
              data-testid="smtp-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="smtp-host" className="text-sm flex items-center gap-2">
              <Server className="w-3.5 h-3.5" />
              Serveur SMTP
            </Label>
            <Input
              id="smtp-host"
              value={smtpConfig.host || ""}
              onChange={(e) => updateField("host", e.target.value)}
              placeholder="smtp.gmail.com"
              data-testid="smtp-host-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="smtp-port" className="text-sm flex items-center gap-2">
              <Hash className="w-3.5 h-3.5" />
              Port
            </Label>
            <Input
              id="smtp-port"
              type="number"
              value={smtpConfig.port || ""}
              onChange={(e) => updateField("port", e.target.value)}
              placeholder="587"
              data-testid="smtp-port-input"
            />
            <div className="flex gap-2 mt-1">
              {[587, 465, 25].map((p) => (
                <Badge
                  key={p}
                  variant={smtpConfig.port === p ? "default" : "outline"}
                  className="cursor-pointer text-xs hover:scale-105 transition-transform"
                  onClick={() => updateField("port", String(p))}
                  data-testid={`port-preset-${p}`}
                >
                  {p}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="smtp-email" className="text-sm flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              Email expéditeur
            </Label>
            <Input
              id="smtp-email"
              type="email"
              value={smtpConfig.email || ""}
              onChange={(e) => updateField("email", e.target.value)}
              placeholder="votre@email.com"
              data-testid="smtp-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="smtp-password" className="text-sm flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" />
              Mot de passe / App Password
            </Label>
            <Input
              id="smtp-password"
              type="password"
              value={smtpConfig.password || ""}
              onChange={(e) => updateField("password", e.target.value)}
              placeholder="Mot de passe d'application"
              data-testid="smtp-password-input"
            />
          </div>

          {/* Test result */}
          {testResult && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                testResult.success
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}
              data-testid="smtp-test-result"
            >
              {testResult.success ? (
                <CheckCircle2 className="w-4 h-4 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 shrink-0" />
              )}
              {testResult.message}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1 gap-2 rounded-full"
              onClick={handleTest}
              disabled={testing}
              data-testid="smtp-test-button"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Tester
            </Button>
            <Button
              className="flex-1 gap-2 rounded-full"
              onClick={handleSave}
              disabled={saving}
              data-testid="smtp-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sauvegarder
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
